import React, { createElement } from 'react';
import { ItemType } from 'antd/es/menu/interface';
import { HomeOutlined } from '@ant-design/icons';
import Link from 'next/link';
import { BreadcrumbItem, DynamicMenuConfig } from './types';
import { defaultPathMappings } from '../config/path-mappings';

/**
 * 创建统一的菜单项 - 合并动态菜单配置
 *
 * @param dynamicConfig 动态菜单配置
 * @returns 统一的菜单项数组
 */
export const createUnifiedMenuItems = (
    dynamicConfig: DynamicMenuConfig,
): ItemType[] => {
    const items: ItemType[] = [];

    // 添加动态菜单配置
    if (dynamicConfig.favorites) {
        items.push(...dynamicConfig.favorites.menus);
    }

    items.push(...dynamicConfig.home.menus);

    if (dynamicConfig.admin) {
        items.push(...dynamicConfig.admin.menus);
    }

    return items;
};

/**
 * 从动态菜单配置中提取所有菜单项 - 用于面包屑生成
 *
 * @param dynamicConfig 动态菜单配置
 * @returns 所有菜单项的扁平数组
 */
export const extractAllMenuItems = (
    dynamicConfig: DynamicMenuConfig,
): ItemType[] => {
    const items: ItemType[] = [];

    // 添加动态菜单配置
    if (dynamicConfig.favorites) {
        items.push(...dynamicConfig.favorites.menus);
    }

    items.push(...dynamicConfig.home.menus);

    if (dynamicConfig.admin) {
        items.push(...dynamicConfig.admin.menus);
    }

    return items;
};

// 向后兼容的函数
export const createBreadcrumbItems = (pathname: string): BreadcrumbItem[] => {
    // 为了向后兼容，这里需要重构为使用新的配置系统
    // 但在实际项目中，应该直接使用 createBreadcrumbItemsNew
    console.warn(
        'createBreadcrumbItems 已弃用，请使用 createBreadcrumbItemsNew',
    );
    return createBreadcrumbItemsNew(pathname, []);
};

/**
 * 通过路径查找菜单项 - 递归遍历完整菜单树
 *
 * @param path 要查找的路径
 * @param menuItems 要搜索的菜单项数组
 * @returns 找到的菜单项或null
 */
export const findMenuItemByPath = (
    path: string,
    menuItems: ItemType[],
): ItemType | null => {
    // 递归搜索菜单项
    const searchInMenuItems = (items: ItemType[]): ItemType | null => {
        for (const item of items) {
            if (item && 'key' in item && item.key === path) {
                return item;
            }
            if (item && 'children' in item && item.children) {
                const found = searchInMenuItems(item.children as ItemType[]);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };

    return searchInMenuItems(menuItems);
};

/**
 * 从菜单项提取标题和链接信息
 *
 * @param item 菜单项
 * @returns 提取的标题、链接和图标信息
 */
export const extractMenuItemInfo = (item: ItemType | null): BreadcrumbItem => {
    let title: React.ReactNode = '未知页面';
    let href: string | undefined = undefined;
    let icon: React.ReactNode | undefined = undefined;

    // 安全地检查item和label属性
    if (item && 'label' in item && item.label) {
        const label = item.label;

        if (typeof label === 'string') {
            title = label;
        } else if (React.isValidElement(label)) {
            // 安全地检查是否为Link组件
            if ('type' in label && label.type === Link && 'props' in label) {
                const linkProps = label.props as {
                    href?: string;
                    children?: React.ReactNode;
                };
                href = linkProps.href as string;
                title = linkProps.children || '未知页面';
            } else if ('props' in label) {
                const elementProps = label.props as {
                    children?: React.ReactNode;
                };
                if (elementProps.children) {
                    title = elementProps.children;
                } else {
                    // 作为最后的手段，转换为字符串
                    try {
                        title = String(label);
                    } catch {
                        title = '未知页面';
                    }
                }
            } else {
                // 作为最后的手段，转换为字符串
                try {
                    title = String(label);
                } catch {
                    title = '未知页面';
                }
            }
        } else {
            // 转换为字符串
            try {
                title = String(label);
            } catch {
                title = '未知页面';
            }
        }
    }

    // 提取图标
    if (item && 'icon' in item && item.icon) {
        icon = item.icon;
    }

    return { title, href, icon };
};

/**
 * 创建面包屑数据 - 基于动态配置和路径映射
 *
 * @param pathname 当前路径
 * @param menuItems 菜单项数组
 * @param pathMappings 特殊路径映射配置
 * @returns 面包屑项数组
 */
/**
 * 比较路径模式和实际路径，忽略[param]占位符
 * @param patternPath 路径模式，如'/control-panel/departments/set-department-manager/[param]'
 * @param actualPath 实际路径，如'/control-panel/departments/set-department-manager/123'
 * @returns 是否匹配
 */
const matchPathWithParams = (
    patternPath: string | undefined,
    actualPath: string | undefined,
): boolean => {
    if (!patternPath || !actualPath) {
        return false;
    }
    // 移除查询参数部分，只比较路径主体
    const cleanPatternPath = patternPath.split('?')[0];
    const cleanActualPath = actualPath.split('?')[0];

    const patternParts = cleanPatternPath.split('/').filter(Boolean);
    const actualParts = cleanActualPath.split('/').filter(Boolean);

    // 如果路径段数量不同，则不匹配
    if (patternParts.length !== actualParts.length) {
        return false;
    }

    // 逐段比较路径
    for (let i = 0; i < patternParts.length; i++) {
        const patternPart = patternParts[i];
        const actualPart = actualParts[i];

        // 如果是[param]占位符，则跳过比较
        if (patternPart.startsWith('[') && patternPart.endsWith(']')) {
            continue;
        }

        // 如果不是占位符且不匹配，则整体不匹配
        if (patternPart !== actualPart) {
            return false;
        }
    }

    return true;
};

export const createBreadcrumbItemsNew = (
    pathname: string,
    menuItems: ItemType[],
): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];
    // 始终添加首页作为面包屑的第一级
    items.push({
        title: '首页',
        href: '/',
        icon: createElement(HomeOutlined),
    });

    // 如果路径不是根路径，处理menu菜单路由，生成面包屑
    if (pathname !== '/') {
        // 处理路径，移除查询参数部分，只保留路径主体
        const cleanPathname = pathname.split('?')[0];
        const pathParts = cleanPathname.split('/').filter(Boolean);
        let currentPath = '/';

        // 处理每个路径部分
        for (let i = 0; i < pathParts.length; i++) {
            currentPath += (i === 0 ? '' : '/') + pathParts[i];

            // 查找当前路径对应的菜单项
            const menuItem = findMenuItemByPath(currentPath, menuItems);
            if (menuItem) {
                // 从菜单项提取标题、链接和图标
                const { title, href, icon } = extractMenuItemInfo(menuItem);
                // 对于最后一级（当前页面），不设置href
                if (i === pathParts.length - 1) {
                    items.push({ title, icon });
                } else {
                    items.push({ title, href, icon });
                }
            } else {
                // 如果没有找到对应菜单项，从特殊路径中查找
                let foundMapping = false;

                // 直接处理当前路径对应的映射
                // 首先检查是否是编辑个人资料页面
                if (currentPath === '/users/edit-profile') {
                    // 添加个人资料作为父级
                    const profileExists = items.some(
                        item => item.href === '/users/profile',
                    );
                    if (!profileExists) {
                        items.push({
                            title: '个人资料',
                            href: '/users/profile',
                        });
                    }
                    // 添加编辑个人资料
                    if (i === pathParts.length - 1) {
                        items.push({ title: '编辑个人资料' });
                    } else {
                        items.push({
                            title: '编辑个人资料',
                            href: currentPath,
                        });
                    }
                    foundMapping = true;
                } else {
                    // 按路径长度排序，优先匹配更长、更具体的路径
                    const sortedMappings = Object.entries(
                        defaultPathMappings,
                    ).sort(([pathA], [pathB]) => pathB.length - pathA.length);

                    for (const [path, mapping] of sortedMappings) {
                        // 使用matchPathWithParams函数进行路径比较，忽略[param]占位符
                        if (matchPathWithParams(path, currentPath)) {
                            // 精确匹配当前完整路径
                            // 如果有上级链接，先添加上级菜单项（如果尚未添加）
                            if (mapping.href) {
                                // 处理动态路径，从当前实际路径中提取参数值
                                let actualParentHref = mapping.href;
                                if (
                                    actualParentHref.includes('[param]') &&
                                    pathname
                                ) {
                                    // 从当前实际路径中提取参数值（路径部分）
                                    const actualPathParts = currentPath
                                        .split('/')
                                        .filter(Boolean);
                                    const mappingPathParts = mapping.href
                                        .split('?')[0]
                                        .split('/')
                                        .filter(Boolean);

                                    // 找到当前路径中对应参数的位置
                                    const paramIndex =
                                        mappingPathParts.findIndex(
                                            part => part === '[param]',
                                        );
                                    if (
                                        paramIndex !== -1 &&
                                        actualPathParts.length > paramIndex
                                    ) {
                                        // 构建实际的父级路径
                                        const paramValue =
                                            actualPathParts[paramIndex];
                                        actualParentHref = mapping.href.replace(
                                            /\[param\]/g,
                                            paramValue,
                                        );
                                    } else {
                                        // 处理查询参数中的 [param]
                                        // 从原始 pathname 中提取查询参数
                                        const queryString =
                                            pathname.split('?')[1];
                                        if (queryString) {
                                            const searchParams =
                                                new URLSearchParams(
                                                    queryString,
                                                );
                                            // 获取所有参数值，按顺序匹配 [param]
                                            const paramValues = Array.from(
                                                searchParams.values(),
                                            );
                                            // 统计 href 中 [param] 的数量
                                            const paramCount = (
                                                mapping.href.match(
                                                    /\[param\]/g,
                                                ) || []
                                            ).length;
                                            // 只取需要的参数数量
                                            const neededParams =
                                                paramValues.slice(
                                                    0,
                                                    paramCount,
                                                );

                                            let resultHref = mapping.href;
                                            let paramIndex = 0;
                                            resultHref = resultHref.replace(
                                                /\[param\]/g,
                                                () => {
                                                    const value =
                                                        neededParams[
                                                            paramIndex
                                                        ];
                                                    paramIndex++;
                                                    return value || '';
                                                },
                                            );
                                            actualParentHref = resultHref;
                                        }
                                    }
                                }

                                // 检查上级链接是否已在面包屑中
                                const parentExists = items.some(item =>
                                    matchPathWithParams(
                                        actualParentHref,
                                        item.href,
                                    ),
                                );
                                if (!parentExists) {
                                    // 递归处理上级路径的上级路径
                                    // 首先检查父级路径是否在 defaultPathMappings 中
                                    let parentTitle = '';
                                    let parentMappingFound = false;

                                    for (const [
                                        parentPath,
                                        parentMapping,
                                    ] of sortedMappings) {
                                        if (
                                            matchPathWithParams(
                                                parentPath,
                                                actualParentHref,
                                            )
                                        ) {
                                            parentTitle = parentMapping.title;
                                            parentMappingFound = true;

                                            // 递归处理父级路径的上级路径
                                            if (parentMapping.href) {
                                                // 处理动态路径，从当前实际路径中提取参数值
                                                let grandParentHref =
                                                    parentMapping.href;
                                                if (
                                                    grandParentHref.includes(
                                                        '[param]',
                                                    ) &&
                                                    pathname
                                                ) {
                                                    // 从原始 pathname 中提取查询参数
                                                    const queryString =
                                                        pathname.split('?')[1];
                                                    if (queryString) {
                                                        const searchParams =
                                                            new URLSearchParams(
                                                                queryString,
                                                            );
                                                        // 获取所有参数值，按顺序匹配 [param]
                                                        const paramValues =
                                                            Array.from(
                                                                searchParams.values(),
                                                            );
                                                        // 统计 href 中 [param] 的数量
                                                        const paramCount = (
                                                            parentMapping.href.match(
                                                                /\[param\]/g,
                                                            ) || []
                                                        ).length;
                                                        // 只取需要的参数数量
                                                        const neededParams =
                                                            paramValues.slice(
                                                                0,
                                                                paramCount,
                                                            );

                                                        let resultHref =
                                                            parentMapping.href;
                                                        let paramIndex = 0;
                                                        resultHref =
                                                            resultHref.replace(
                                                                /\[param\]/g,
                                                                () => {
                                                                    const value =
                                                                        neededParams[
                                                                            paramIndex
                                                                        ];
                                                                    paramIndex++;
                                                                    return (
                                                                        value ||
                                                                        ''
                                                                    );
                                                                },
                                                            );
                                                        grandParentHref =
                                                            resultHref;
                                                    }
                                                }

                                                // 检查祖父级链接是否已在面包屑中
                                                const grandParentExists =
                                                    items.some(item =>
                                                        matchPathWithParams(
                                                            grandParentHref,
                                                            item.href,
                                                        ),
                                                    );
                                                if (!grandParentExists) {
                                                    // 查找祖父级路径的映射
                                                    let grandParentTitle = '';
                                                    for (const [
                                                        grandParentPath,
                                                        grandParentMapping,
                                                    ] of sortedMappings) {
                                                        if (
                                                            matchPathWithParams(
                                                                grandParentPath,
                                                                grandParentHref,
                                                            )
                                                        ) {
                                                            grandParentTitle =
                                                                grandParentMapping.title;
                                                            break;
                                                        }
                                                    }

                                                    const grandParentMenuItem =
                                                        findMenuItemByPath(
                                                            grandParentHref,
                                                            menuItems,
                                                        );

                                                    if (grandParentMenuItem) {
                                                        const { title, href } =
                                                            extractMenuItemInfo(
                                                                grandParentMenuItem,
                                                            );
                                                        items.push({
                                                            title,
                                                            href,
                                                        });
                                                    } else if (
                                                        grandParentTitle
                                                    ) {
                                                        // 如果祖父级路径在 defaultPathMappings 中，使用其标题
                                                        items.push({
                                                            title: grandParentTitle,
                                                            href: grandParentHref,
                                                        });
                                                    } else {
                                                        // 从URL路径中提取祖父级路径段作为标题
                                                        const hrefParts =
                                                            grandParentHref
                                                                .split('/')
                                                                .filter(
                                                                    Boolean,
                                                                );
                                                        const titleSegment =
                                                            hrefParts.length > 0
                                                                ? hrefParts[
                                                                      hrefParts.length -
                                                                          1
                                                                  ]
                                                                : '未知页面';
                                                        const formattedTitle =
                                                            titleSegment
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                            titleSegment
                                                                .slice(1)
                                                                .replace(
                                                                    /-/g,
                                                                    ' ',
                                                                );
                                                        items.push({
                                                            title: formattedTitle,
                                                            href: grandParentHref,
                                                        });
                                                    }
                                                }
                                            }
                                            break;
                                        }
                                    }

                                    if (!parentMappingFound) {
                                        const parentMenuItem =
                                            findMenuItemByPath(
                                                actualParentHref,
                                                menuItems,
                                            );

                                        if (parentMenuItem) {
                                            const { title, href } =
                                                extractMenuItemInfo(
                                                    parentMenuItem,
                                                );
                                            items.push({ title, href });
                                        } else {
                                            // 从URL路径中提取父级路径段作为标题
                                            const hrefParts = actualParentHref
                                                .split('/')
                                                .filter(Boolean);
                                            const titleSegment =
                                                hrefParts.length > 0
                                                    ? hrefParts[
                                                          hrefParts.length - 1
                                                      ]
                                                    : '未知页面';
                                            const formattedTitle =
                                                titleSegment
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                titleSegment
                                                    .slice(1)
                                                    .replace(/-/g, ' ');
                                            items.push({
                                                title: formattedTitle,
                                                href: actualParentHref,
                                            });
                                        }
                                    } else {
                                        // 如果父级路径在 defaultPathMappings 中，使用其标题
                                        items.push({
                                            title: parentTitle,
                                            href: actualParentHref,
                                        });
                                    }
                                }
                            }
                            // 添加当前页面标题（不包含链接，因为是当前页面）
                            if (i === pathParts.length - 1) {
                                items.push({ title: mapping.title });
                            } else {
                                // 使用映射中的路径（包含 [param] 占位符）作为基础路径
                                let href = path;
                                // 如果路径包含 [param]，需要从查询参数中提取值进行替换
                                if (href.includes('[param]') && pathname) {
                                    const queryString = pathname.split('?')[1];
                                    if (queryString) {
                                        const searchParams =
                                            new URLSearchParams(queryString);
                                        // 获取所有参数值，按顺序匹配 [param]
                                        const paramValues = Array.from(
                                            searchParams.values(),
                                        );
                                        // 统计路径中 [param] 的数量
                                        const paramCount = (
                                            path.match(/\[param\]/g) || []
                                        ).length;
                                        // 只取需要的参数数量
                                        const neededParams = paramValues.slice(
                                            0,
                                            paramCount,
                                        );

                                        let resultHref = href;
                                        let paramIndex = 0;
                                        resultHref = resultHref.replace(
                                            /\[param\]/g,
                                            () => {
                                                const value =
                                                    neededParams[paramIndex];
                                                paramIndex++;
                                                return value || '';
                                            },
                                        );
                                        href = resultHref;
                                    }
                                }
                                items.push({
                                    title: mapping.title,
                                    href: href,
                                });
                            }
                            foundMapping = true;
                            break; // 找到一个匹配的映射后就停止搜索，避免重复添加
                        }
                    }
                }
            }
        }
    }

    // 去重：移除重复的面包屑项（根据标题去重）
    const seen = new Set<string>();
    const uniqueItems = items.filter(item => {
        const title = typeof item.title === 'string' ? item.title : '';
        if (seen.has(title)) {
            return false;
        }
        seen.add(title);
        return true;
    });

    return uniqueItems;
};
